import { Cake } from "lucide-react";
import {
  useCanAccess,
  useGetIdentity,
  useGetList,
  useTranslate,
} from "ra-core";

import { Card } from "@/components/ui/card";

import { SimpleList } from "../simple-list/SimpleList";
import { ListErrorState } from "../design/ListStates";
import { APP_LOCALE } from "../misc/locale";
import type { Client } from "../types";
import {
  computeUpcomingBirthdays,
  type UpcomingBirthday,
} from "./upcomingBirthdaysWindow";

/** Day + month only, no year — matches the AML rule against surfacing age. */
const DAY_MONTH_FORMAT = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "numeric",
  month: "long",
});

const formatDayMonth = (row: UpcomingBirthday) =>
  DAY_MONTH_FORMAT.format(new Date(2001, row.month - 1, row.day));

/**
 * "Cumpleaños próximos" dashboard card (Almikia Epic E3, TASK-024): the
 * current user's own clients with a birthday in the next 30 days. Owner-only
 * by construction — the data-provider filter is `sales_id` (the current
 * user's own sale id) plus the non-PII `birthday@not.is: null`, so no client
 * field value ever reaches a URL or query string. No totals, no counts, no
 * cross-advisor comparison (independence guide).
 */
export const UpcomingBirthdays = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { canAccess, isPending: isCanAccessPending } = useCanAccess({
    resource: "clients",
    action: "list",
  });

  const {
    data: clients,
    isPending: isClientsPending,
    error: clientsError,
    refetch: refetchClients,
  } = useGetList<Client>(
    "clients",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: { sales_id: identity?.id, "birthday@not.is": null },
    },
    { enabled: !!canAccess && Number.isInteger(identity?.id) },
  );

  if (isCanAccessPending || !canAccess) return null;

  const rows = isClientsPending ? [] : computeUpcomingBirthdays(clients ?? []);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <Cake className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("resources.clients.upcoming_birthdays.title")}
        </h2>
      </div>
      {clientsError ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={() => refetchClients()}
        />
      ) : (
        <Card className="py-0">
          <SimpleList<UpcomingBirthday>
            linkType="show"
            data={rows}
            total={rows.length}
            isPending={isClientsPending}
            resource="clients"
            className="[&>li:first-child>a]:rounded-t-xl [&>li:last-child>a]:rounded-b-xl"
            primaryText={(row) => row.full_legal_name}
            secondaryText={(row) => formatDayMonth(row)}
            empty={
              <div className="p-4">
                <p className="text-sm">
                  {translate("resources.clients.upcoming_birthdays.empty")}
                </p>
              </div>
            }
          />
        </Card>
      )}
    </div>
  );
};
